import { supabase } from "@/integrations/supabase/client";

export interface SupplierRuleCriteria {
  licensePlate?: {
    type: "exact" | "regex";
    value: string;
  };
}

export interface SupplierRuleAllocation {
  expenseAccount: string;
  description?: string;
}

export interface SupplierRule {
  criteria: SupplierRuleCriteria;
  allocation: SupplierRuleAllocation;
}

/**
 * Tipo de operación IVA según códigos A3 (pos 100-101 del registro 9 SUENLACE):
 *   interior    → "01" Operaciones interiores IVA deducible (default)
 *   aib         → "03" Adquisición intracomunitaria de bienes  (alimenta modelo 349)
 *   ais         → "08" Adquisición intracomunitaria de servicios (alimenta modelo 349)
 *   isp         → "04" Inversión del sujeto pasivo (art. 84 LIVA)
 *   importacion → "06" Importación (la cuota la liquida la aduana en el DUA)
 *
 * Para aib/ais/isp el SUENLACE emite cuentas 477/472 en pos 192/216 para que A3
 * genere automáticamente el doble asiento (auto-repercusión).
 */
export type SupplierOperationType = "interior" | "aib" | "ais" | "isp" | "importacion";

export const OPERATION_TYPE_OPTIONS: { value: SupplierOperationType; label: string; short: string }[] = [
  { value: "interior",    label: "Interior",                         short: "Interior" },
  { value: "aib",         label: "Intracomunitario — bienes",        short: "Intra. bienes" },
  { value: "ais",         label: "Intracomunitario — servicios",     short: "Intra. servicios" },
  { value: "isp",         label: "Inversión del sujeto pasivo",      short: "ISP" },
  { value: "importacion", label: "Importación (DUA)",                short: "Importación" },
];

export const normalizeOperationType = (raw: unknown): SupplierOperationType => {
  const v = String(raw ?? "").toLowerCase().trim();
  return v === "aib" || v === "ais" || v === "isp" || v === "importacion" ? v : "interior";
};

/**
 * Tipo de retención del proveedor. Determina el código de impreso que se escribe
 * en la posición 173-174 del registro 9 del SUENLACE:
 *   mod111 → se deja en blanco para que A3 tipifique el perceptor internamente
 *   mod115 → impreso 03 (modelo 180, clave A dinerarias)
 *            (arrendamientos de inmuebles)
 *   mod193 → impreso 28 (modelo 193, clave C04 dinerarias)
 *            (alquileres mobiliarios / modelo 123 en la app)
 */
export type SupplierRetentionType = "none" | "mod111" | "mod115" | "mod193";

export const RETENTION_TYPE_OPTIONS: {
  value: SupplierRetentionType;
  label: string;
  short: string;
}[] = [
  { value: "none",   label: "Sin retención",                                    short: "—" },
  { value: "mod111", label: "Modelo 111 — profesionales / trabajo",             short: "111" },
  { value: "mod115", label: "Modelo 115 — alquiler inmobiliario (impreso 180 dinerarias)", short: "115/180" },
  { value: "mod193", label: "Modelo 123 — alquiler mobiliario (impreso 193, clave C04 dinerarias)", short: "123" },
];

export const normalizeRetentionType = (raw: unknown): SupplierRetentionType => {
  const v = String(raw ?? "").toLowerCase().trim();
  // Valores heredados del esquema anterior
  if (v === "profesionales" || v === "mod111" || v === "111") return "mod111";
  if (v === "arrendamientos" || v === "capital_mobiliario" || v === "mod115" || v === "115" || v === "180")
    return "mod115";
  if (v === "mod193" || v === "193" || v === "mod123" || v === "123" || v === "mobiliario" || v === "alquiler_mobiliario")
    return "mod193";
  return "none";
};

export interface SupplierMasterEntry {
  accountCode: string;
  expenseAccount: string;
  description: string;
  operationType: SupplierOperationType;
  /** Tipo de retención (clasificación 111/115/180/agraria). */
  retentionType?: SupplierRetentionType;
  /** Código postal (5 dígitos) del proveedor. Necesario para el modelo 347. */
  postalCode?: string;
  rules?: SupplierRule[];
}


export type CompanyNamingMode = "standard" | "original_numbered";

export interface CompanyMaster {
  id: string;
  name: string;
  a3_company_code?: string | null;
  tax_id?: string | null;
  organization_id?: string | null;
  naming_mode?: CompanyNamingMode;
}

const normalizeNif = (nif: string): string =>
  (nif || "")
    .normalize("NFKC")
    .replace(/[^A-Z0-9]/gi, "")
    .toUpperCase()
    .replace(/^ES(?=[A-Z0-9]{9}$)/, "");

const normalizePlate = (plate: string): string =>
  plate.replace(/[\s\-]/g, "").toUpperCase();

const parseRules = (raw: unknown): SupplierRule[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr = Array.isArray(raw) ? raw : typeof raw === "string" ? JSON.parse(raw) : [];
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    const valid: SupplierRule[] = [];
    for (const r of arr) {
      if (!r || typeof r !== "object") continue;
      const allocation = (r as any).allocation;
      const criteria = (r as any).criteria;
      if (!allocation || typeof allocation.expenseAccount !== "string") continue;
      valid.push({
        criteria: {
          licensePlate: criteria?.licensePlate
            ? {
                type: criteria.licensePlate.type === "regex" ? "regex" : "exact",
                value: String(criteria.licensePlate.value || ""),
              }
            : undefined,
        },
        allocation: {
          expenseAccount: allocation.expenseAccount,
          description: typeof allocation.description === "string" ? allocation.description : undefined,
        },
      });
    }
    return valid.length > 0 ? valid : undefined;
  } catch (e) {
    console.warn("Invalid supplier rules JSON, ignoring:", e);
    return undefined;
  }
};

export const loadCompanyMasters = async (
  organizationId?: string,
): Promise<CompanyMaster[]> => {
  let query = supabase
    .from("company_masters")
    .select("id, name, a3_company_code, tax_id, organization_id, naming_mode")
    .order("name");

  if (organizationId) {
    query = query.eq("organization_id", organizationId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error loading company masters:", error);
    return [];
  }
  return (data || []).map((r: any) => ({
    id: r.id,
    name: r.name,
    a3_company_code: r.a3_company_code ?? null,
    tax_id: r.tax_id ?? null,
    organization_id: r.organization_id ?? null,
    naming_mode: (r.naming_mode === "original_numbered" ? "original_numbered" : "standard") as CompanyNamingMode,
  }));
};

export const loadSupplierMaster = async (companyId?: string): Promise<Map<string, SupplierMasterEntry>> => {
  const map = new Map<string, SupplierMasterEntry>();
  const PAGE = 1000;
  let from = 0;
  const all: any[] = [];

  while (true) {
    let query = supabase
      .from("supplier_master")
      .select("account_code, tax_id, expense_account, description, rules, operation_type, postal_code, retention_type")
      .range(from, from + PAGE - 1);

    if (companyId) query = query.eq("company_id", companyId);

    const { data, error } = await query;
    if (error) {
      console.error("Error loading supplier master:", error);
      return map;
    }
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE) break;
    from += PAGE;
  }

  for (const row of all) {
    const nif = normalizeNif(row.tax_id || "");
    if (nif) {
      map.set(nif, {
        accountCode: row.account_code || "",
        expenseAccount: row.expense_account || "",
        description: row.description || "",
        operationType: normalizeOperationType((row as { operation_type?: string }).operation_type),
        retentionType: normalizeRetentionType((row as { retention_type?: string }).retention_type),
        postalCode: ((row as { postal_code?: string }).postal_code || "").replace(/\D/g, "").slice(0, 5) || undefined,
        rules: parseRules((row as { rules?: unknown }).rules),
      });
    }
  }

  return map;
};

export const lookupSupplier = (
  master: Map<string, SupplierMasterEntry>,
  taxId: string,
  context?: { licensePlate?: string }
): SupplierMasterEntry | undefined => {
  if (!taxId) return undefined;
  const entry = master.get(normalizeNif(taxId));
  if (!entry) return undefined;

  const plate = context?.licensePlate ? normalizePlate(context.licensePlate) : "";
  if (!entry.rules || entry.rules.length === 0 || !plate) return entry;

  for (const rule of entry.rules) {
    const lp = rule.criteria.licensePlate;
    if (!lp || !lp.value) continue;
    let matched = false;
    if (lp.type === "regex") {
      try {
        matched = new RegExp(lp.value, "i").test(plate);
      } catch (e) {
        console.warn(`Invalid regex in supplier rule for NIF ${taxId}:`, lp.value, e);
        continue;
      }
    } else {
      matched = normalizePlate(lp.value) === plate;
    }
    if (matched) {
      return {
        ...entry,
        expenseAccount: rule.allocation.expenseAccount,
        description: rule.allocation.description ?? entry.description,
      };
    }
  }

  return entry;
};
